import React, { useState, useEffect, useRef, useCallback } from 'react'
import { branding } from '../content/branding'
import DesktopNav from './DesktopNav'
import SidecarMenu from './SidecarMenu'
import { useNavigation } from '../contexts/NavigationContext'

/** True when the first snap section is dark (transparent header needs light chrome). */
function firstSectionIsOnDark() {
  const first = document.querySelector('.snap-section')
  return first?.getAttribute('data-tone') === 'onDark'
}

export default function Header() {
  const { sidecarOpen, setSidecarOpen, setHeaderVisible } = useNavigation()
  const [scrolled, setScrolled] = useState(false)
  const [overDark, setOverDark] = useState(false)
  const lastScrollYRef = useRef(0)

  const refreshOverDark = useCallback(() => {
    setOverDark(firstSectionIsOnDark())
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY
      setScrolled(currentScrollY > 40)

      const isScrollingDown = currentScrollY - lastScrollYRef.current > 0

      if (isScrollingDown && currentScrollY > 100) {
        setHeaderVisible(false)
      } else {
        setHeaderVisible(true)
      }

      lastScrollYRef.current = currentScrollY
    }

    // Initial state (handles restored scroll + first-section tone once painted)
    onScroll()
    refreshOverDark()
    // Hero mounts after first paint — re-check tone shortly after
    const t = window.setTimeout(refreshOverDark, 50)

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.clearTimeout(t)
    }
  }, [setHeaderVisible, refreshOverDark])

  // Site / section order changes remount main content; re-read first-section tone
  useEffect(() => {
    refreshOverDark()
    const root = document.querySelector('main') || document.body
    const mo = new MutationObserver(() => refreshOverDark())
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tone'],
    })
    return () => mo.disconnect()
  }, [refreshOverDark])

  const { headerVisible } = useNavigation()

  // Light chrome only while transparent over a dark first section; glass uses dark assets
  const lightChrome = !scrolled && overDark

  return (
    <>
      <header
        data-testid="site-header"
        data-scrolled={scrolled ? 'true' : 'false'}
        data-over-dark={overDark ? 'true' : 'false'}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-header-scrolled py-3 shadow-sm backdrop-blur-md'
            : 'bg-transparent py-4'
        } ${headerVisible ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-8 xl:px-8">
          <div className="flex items-center justify-between">

            {/* Logo — light assets when transparent over dark hero; dark on glass / light pages */}
            <a
              href="/"
              data-testid="site-logo"
              className={`hover:opacity-70 transition-opacity h-9 sm:h-8 md:h-7 lg:h-7 min-w-[180px] sm:min-w-[170px] md:min-w-[160px] font-primary text-sm md:text-base ${
                lightChrome ? 'text-primary-on-dark' : 'text-color-primary'
              }`}
            >
              {/* Mobile & Tablet (<1024px): circular white asset; invert only on cream/glass */}
              <img
                src={branding.logo.circularLight}
                alt={branding.logo.alt}
                className={`lg:hidden h-8 w-8 object-contain ${lightChrome ? '' : 'invert'}`}
                loading="eager"
                fetchPriority="high"
                width="32"
                height="32"
              />
              {/* Desktop (>=1024px): wordmark light on dark hero, dark on glass */}
              <img
                src={lightChrome ? branding.logo.wordmarkLight : branding.logo.wordmarkDark}
                alt={branding.logo.alt}
                className="hidden lg:block h-full w-auto object-contain"
                loading="eager"
                fetchPriority="high"
                width="180"
                height="28"
              />
            </a>

            {/* Desktop Nav with inline Language Switcher */}
            <div className="hidden 900:flex items-center flex-1 justify-center -ml-[20%]">
              <DesktopNav onDark={lightChrome} />
            </div>
          </div>
        </div>
      </header>

      {/* Sidecar Menu - all breakpoints */}
      <SidecarMenu isOpen={sidecarOpen} onClose={() => setSidecarOpen(false)} />
    </>
  )
}
